import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";

const siteUrl = import.meta.env.VITE_SITE_URL;

const articleJsonLd = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "OOPBUY Spreadsheet Guide – Alles, was du wissen musst",
  "description": "Willkommen zum umfassendsten deutschsprachigen Guide zur OOPBUY Spreadsheet. Egal ob Anfänger oder Profi – hier erfährst du alles über die besten Deals, QC und Versand.",
  "url": `${siteUrl}/oopbuy-spreadsheet-guide`,
  "image": "https://readdy.ai/api/search-image?query=Overhead%20flat%20lay%20of%20luxury%20fashion%20items%20including%20designer%20sneakers%20and%20leather%20accessories%20on%20warm%20charcoal%20textured%20surface%2C%20moody%20editorial%20lighting%20with%20golden%20accent%20highlights%2C%20high-end%20fashion%20product%20photography%2C%20rich%20textures%20and%20shadows%2C%20warm%20amber%20undertones%2C%20no%20branding%20visible&width=1600&height=700&seq=article-hero-001-v2&orientation=landscape",
  "datePublished": "2026-07-16",
  "dateModified": "2026-07-16",
  "author": {
    "@type": "Organization",
    "name": "OOPBUY Spreadsheet Deutschland"
  },
  "publisher": {
    "@type": "Organization",
    "name": "OOPBUY Spreadsheet Deutschland",
    "url": siteUrl,
    "logo": {
      "@type": "ImageObject",
      "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/5133451b25e408e4a25e4df97e05add8.jpeg"
    }
  },
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": `${siteUrl}/oopbuy-spreadsheet-guide`
  }
};

export default function Article() {
  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <Navbar />

      <article className="pt-20 md:pt-24">
        {/* Hero Header */}
        <header className="relative bg-secondary-900 py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Overhead%20flat%20lay%20of%20luxury%20fashion%20items%20including%20sneakers%2C%20watches%2C%20sunglasses%20and%20small%20leather%20goods%20on%20dark%20charcoal%20textured%20surface%2C%20moody%20editorial%20lighting%2C%20warm%20accent%20highlights%2C%20high-end%20fashion%20photography&width=1600&height=700&seq=article-hero-001&orientation=landscape"
              alt="OOPBUY Spreadsheet Guide"
              className="w-full h-full object-cover object-top opacity-40"
            />
            <div className="absolute inset-0 bg-secondary-900/70"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-xs font-semibold text-accent-400 uppercase tracking-[0.2em] mb-4">
                OOPBUY Guide 2026
              </p>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-4">
                OOPBUY Spreadsheet Guide
                <br />
                <span className="text-accent-400">Alles, was du wissen musst</span>
              </h1>
              <p className="text-sm text-background-300">
                Letztes Update: 16. Juli 2026 · Lesezeit: 8 Minuten
              </p>
            </div>
          </div>
        </header>

        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-8 lg:px-12 py-4 bg-background-100 border-b border-background-200/70">
          <div className="max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-600 transition-colors">
                Home
              </Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <span className="text-foreground-700">OOPBUY Spreadsheet Guide</span>
            </nav>
          </div>
        </div>

        {/* Article Content */}
        <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-16">
          <div className="max-w-3xl mx-auto">
            <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 space-y-5 leading-relaxed">
              <p className="text-lg md:text-xl text-foreground-800 font-medium leading-relaxed">
                Willkommen zum umfassendsten deutschsprachigen Guide zur{" "}
                <strong>OOPBUY Spreadsheet</strong>. Egal, ob du gerade erst anfängst oder
                bereits Erfahrung mit chinesischen Shopping-Agenten hast — in diesem Artikel
                erfährst du alles, was du brauchst, um die besten Deals zu finden, Qualität zu
                prüfen und sicher nach Deutschland zu liefern.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                Was ist OOPBUY und warum ist es so beliebt?
              </h2>
              <p>
                <strong>OOPBUY</strong> ist einer der führenden Shopping-Agenten für chinesische
                Marktplätze wie Taobao, Weidian und 1688. Die Plattform fungiert als
                Zwischenhändler: Du bestellst Produkte auf chinesischen Websites, OOPBUY kauft
                sie für dich ein, prüft die Qualität per Fotos (QC) und versendet das Paket an
                deine deutsche Adresse. Besonders bei Sneakern, Streetwear und Accessoires hat
                sich OOPBUY einen Namen gemacht, da viele europäische Käufer sonst keinen Zugang
                zu chinesischen Verkäufern hätten.
              </p>
              <p>
                Die Beliebtheit von OOPBUY liegt in drei Faktoren: erstens der riesigen Auswahl
                an Produkten, die in Europa oft nicht verfügbar sind; zweitens den
                wettbewerbsfähigen Preisen; und drittens dem verlässlichen QC-Prozess, der
                sicherstellt, dass du keine Überraschungen beim Öffnen deines Pakets erlebst.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                Was genau ist eine OOPBUY Spreadsheet?
              </h2>
              <p>
                Eine <strong>OOPBUY Spreadsheet</strong> ist nichts anderes als eine öffentlich
                zugängliche, strukturierte Liste empfohlener Produkte und Verkäufer. Statt
                stundenlang Taobao nach vertrauenswürdigen Shops zu durchsuchen, findest du in
                einer gut gepflegten Spreadsheet bereits vorgefilterte Artikel mit:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Direkten Links zu Taobao-, Weidian- oder 1688-Angeboten</li>
                <li>Preisangaben in verschiedenen Währungen</li>
                <li>Verkäuferbewertungen und Erfahrungsberichten</li>
                <li>QC-Fotos (Qualitätskontrolle) von früheren Käufern</li>
                <li>Kategorisierung nach Produkttypen wie Sneaker, Hoodies, T-Shirts, Taschen</li>
              </ul>
              <p>
                Die{" "}
                <Link
                  to="/"
                  className="text-primary-600 hover:text-primary-700 underline font-medium"
                >
                  größte OOPBUY Spreadsheet auf unserer Homepage
                </Link>{" "}
                umfasst aktuell über 3.000 handverlesene Produkte von verifizierten Verkäufern
                und wird täglich aktualisiert.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                Die wichtigsten Kategorien im Überblick
              </h2>
              <p>
                Eine gute Spreadsheet deckt alle relevanten Produktbereiche ab. Bei OOPBUY sind
                das vor allem Mode und Lifestyle-Artikel. Hier die beliebtesten Kategorien:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-4 not-prose">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 p-3 bg-background-100 rounded-lg border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/30 transition-all"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className={cat.icon}></i>
                    </span>
                    <span className="text-sm font-medium text-foreground-800">{cat.name}</span>
                  </a>
                ))}
              </div>
              <p>
                Jede dieser Kategorien enthält Dutzende bis Hunderte geprüfter Artikel. Ob du
                nach{" "}
                <a
                  href="https://kaspreadsheet.com/category-911-Shoes.html"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-primary-600 hover:text-primary-700 underline"
                >
                  Sneakern
                </a>
                ,{" "}
                <a
                  href="https://kaspreadsheet.com/search-912/name/Shoes.html"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-primary-600 hover:text-primary-700 underline"
                >
                  Hoodies
                </a>{" "}
                oder{" "}
                <a
                  href="https://kaspreadsheet.com/search-921/name/Shoes.html"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-primary-600 hover:text-primary-700 underline"
                >
                  Designer-Taschen
                </a>{" "}
                suchst — in der Spreadsheet findest du verifizierte Empfehlungen.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                Schritt für Schritt: So nutzt du die OOPBUY Spreadsheet
              </h2>
              <p>
                <strong>Schritt 1 — Durchsuchen:</strong> Öffne die Spreadsheet und filtere nach
                deiner gewünschten Kategorie. Nutze die Suchfunktion oder stöbere gezielt durch
                die einzelnen Tabs.
              </p>
              <p>
                <strong>Schritt 2 — Produkt auswählen:</strong> Wenn du einen interessanten
                Artikel findest, klicke auf den Link. Du wirst direkt zum chinesischen
                Marktplatz weitergeleitet, wo du das Produkt in deinen OOPBUY-Warenkorb legen
                kannst.
              </p>
              <p>
                <strong>Schritt 3 — Bestellen:</strong> Füge den Link in OOPBUY ein, wähle
                Größe, Farbe und alle gewünschten Optionen. Bezahle über eine der verfügbaren
                Zahlungsmethoden.
              </p>
              <p>
                <strong>Schritt 4 — QC-Fotos abwarten:</strong> Sobald das Produkt im OOPBUY-Lager
                ankommt, machst du QC-Fotos. Prüfe sorgfältig, ob alles deinen Erwartungen
                entspricht. Bei Problemen kannst du den Artikel zurückgeben oder umtauschen.
              </p>
              <p>
                <strong>Schritt 5 — Versand bestätigen:</strong> Bist du mit der Qualität
                zufrieden, bestätigst du den internationalen Versand. Wähle eine Versandart
                (z. B. DHL, EMS, EUB) und warte auf dein Paket in Deutschland.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                Versand nach Deutschland: Was du wissen musst
              </h2>
              <p>
                Der Versand von China nach Deutschland ist ein zentraler Punkt beim Einkauf
                über OOPBUY. Die Lieferzeit hängt stark von der gewählten Versandmethode ab:
                EUB und EMS dauern in der Regel 10–20 Tage, während DHL oft 7–14 Tage
                benötigt. Beachte, dass Zollabgaben anfallen können, sobald der Warenwert die
                Freigrenze überschreitet.
              </p>
              <p>
                OOPBUY bietet verschiedene Versandlinien an, die speziell auf europäische
                Länder abgestimmt sind. Die sogenannten „Tax-Free"-Lines sind besonders beliebt,
                da sie das Risiko von Zollproblemen minimieren. Für Deutschland empfehlen
                erfahrene Käufer meist die EU-Linien, da diese einen stabilen Tracking-Verlauf
                und zuverlässige Zustellung bieten.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                QC-Prozess: Qualitätskontrolle bei OOPBUY
              </h2>
              <p>
                Der <strong>QC-Prozess</strong> (Quality Control) ist einer der größten Vorteile
                von OOPBUY. Nach dem Eintreffen deiner Ware im Lager werden mehrere Fotos von
                allen relevanten Winkeln gemacht — oft inklusive Verpackung, Etiketten,
                Stickereien und Materialdetails. So kannst du vor dem internationalen Versand
                entscheiden, ob du die Ware behalten möchtest.
              </p>
              <p>
                Besonders bei Sneakern und Kleidung ist der QC-Schritt entscheidend. Kleine
                Unterschiede in der Farbe, das Gewicht der Sohle oder die Qualität der Nähte
                lassen sich oft nur auf detaillierten Fotos erkennen. Nimm dir Zeit, die Bilder
                genau anzusehen, und zögere nicht, bei Unstimmigkeiten einen Umtausch zu
                beantragen.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                Coupons und Gutscheine nutzen
              </h2>
              <p>
                OOPBUY bietet regelmäßig <strong>Coupons</strong> und Rabattaktionen an. Als
                Neukunde erhältst du oft einen Willkommensgutschein, der die ersten
                Versandkosten reduziert. Stammkunden profitieren von saisonalen Aktionen,
                Cashback-Programmen und exklusiven Deals.
              </p>
              <p>
                Achte auf die Coupon-Sektion im OOPBUY-Dashboard, bevor du eine Bestellung
                abschickst. Manche Gutscheine gelten nur für bestimmte Versandlinien oder
                Mindestbestellwerte. Eine kurze Recherche vor dem Checkout kann dir also
                schnell 10–20 % sparen.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                Fazit: Lohnt sich die OOPBUY Spreadsheet?
              </h2>
              <p>
                Absolut. Eine gut gepflegte <strong>OOPBUY Spreadsheet</strong> spart dir
                unzählige Stunden an Recherche und reduziert das Risiko, bei unseriösen
                Verkäufern zu landen. Besonders für Käufer in Deutschland, die auf der Suche
                nach Sneakern, Streetwear und Accessoires aus China sind, ist sie ein
                unverzichtbares Werkzeug.
              </p>
              <p>
                Nutze die{" "}
                <Link
                  to="/"
                  className="text-primary-600 hover:text-primary-700 underline font-medium"
                >
                  OOPBUY Spreadsheet auf unserer Startseite
                </Link>{" "}
                , um direkt loszulegen. Sie wird täglich aktualisiert und enthält ausschließlich
                verifizierte Produkte von vertrauenswürdigen Verkäufern. Viel Erfolg beim
                Smart Shopping!
              </p>

              {/* Internal Link Box */}
              <div className="not-prose mt-10 p-5 md:p-6 bg-primary-50 rounded-xl border border-primary-200/50">
                <h3 className="font-heading text-base md:text-lg font-semibold text-primary-800 mb-3">
                  Weiterlesen
                </h3>
                <ul className="space-y-2">
                  <li>
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 text-sm text-primary-700 hover:text-primary-800 transition-colors"
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-line"></i>
                      </span>
                      Zurück zur Startseite — Alle Kategorien und Produkte
                    </Link>
                  </li>
                  <li>
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-2 text-sm text-primary-700 hover:text-primary-800 transition-colors"
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-external-link-line"></i>
                      </span>
                      Direkt zu OOPBUY — Jetzt einkaufen
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}